import { useContentfulLiveUpdates } from '@contentful/live-preview/react';
import type { GetStaticProps } from 'next';

import Page from '@/components/Page/Page';
import { PageContent, removeLeadingSlash } from '@/contentful';
import {
  getAllPaths,
  getPageContentByPath,
} from '@/contentful/api/contentfulClientApi';
import { isBlogCategoryType } from '@/utils/blog';

interface Props {
  pageContent: PageContent;
  preview: boolean;
}

const emptyPaths = {
  paths: [],
  fallback: 'blocking',
};

export default function Path({ pageContent, preview }: Props) {
  let updatedPageContent = pageContent;
  if (preview && !isBlogCategoryType(pageContent.fields.pageContent)) {
    updatedPageContent = useContentfulLiveUpdates(pageContent);
  }

  return (
    <>
      <div className='blankDiv' id='blankDiv'></div>
      <Page content={updatedPageContent} />
    </>
  );
}

export async function getStaticPaths() {
  const isPreviewMode: boolean = process.env.SKIP_NEXT_PATHS === 'true';

  // skip if this is a preview mode deploy action
  if (isPreviewMode) {
    return emptyPaths;
  }

  const paths = await getAllPaths(isPreviewMode);

  return {
    paths: paths.map((path: string) => {
      return {
        // Next expects array of path pieces without slashes
        params: { path: removeLeadingSlash(path).split('/') },
      };
    }),
    // Since preview mode runs on a next server and production uses next export, this will only affect preview mode
    // Allows for new pages not in the initial build to be previewed
    fallback: 'blocking',
  };
}

export const getStaticProps: GetStaticProps = async ({ params, preview }) => {
  let path = '/';
  if (params?.path && Array.isArray(params.path)) {
    path = `/${params.path.join('/')}`;
  }

  const entry: PageContent = await getPageContentByPath(path, preview);

  return {
    props: {
      preview: preview || false,
      pageContent: entry,
    },
  };
};
